<template>
    <main class="mb-5 pb-5">
        <div class="my-6">
            <h1 class="text-3xl text-theme-base capitalize">
                {{ itemComponent.firstName }} {{ itemComponent.lastName }}
            </h1>
            <p class="text-sm text-theme-muted mt-1 flex items-center gap-1">
                #<uuid-display :value="itemComponent.id" />
            </p>
        </div>
        <custom-navbar
            v-if="id"
            id="tabs-orders"
            v-model="activeTab"
            center-collapsed
            not-line
        >
            <template #left>
                <custom-navbar-item id="home">
                    <router-link
                        to="/app/customers"
                        class="rounded-full bg-brand-blue flex justify-center items-center p-2"
                    >
                        <span>🏠</span>
                    </router-link>
                </custom-navbar-item>

                <custom-navbar-item
                    id="general"
                    :active="['general', 'home'].includes(activeTab)"
                    class="px-2 md:px-4 xl:px-6"
                >
                    <div class="flex">
                        <span class="lg:block hidden">
                            Información general
                        </span>
                        <span>&nbsp;⚙</span>
                    </div>
                </custom-navbar-item>

                <custom-navbar-item
                    v-if="$ability.hasPermission('customer:ListOrders')"
                    id="orders"
                    :active="activeTab === 'orders'"
                    class="px-2 md:px-4 xl:px-6"
                >
                    <div class="flex">
                        <span class="lg:block hidden"> Ordenes </span>
                        <span>&nbsp;🛍️</span>
                    </div>
                </custom-navbar-item>

                <custom-navbar-item
                    v-if="$ability.hasPermission('location:List')"
                    id="locations"
                    :active="activeTab === 'locations'"
                    class="px-2 md:px-4 xl:px-6"
                >
                    <div class="flex">
                        <span class="lg:block hidden"> Direcciones </span>
                        <span>&nbsp;🌎</span>
                    </div>
                </custom-navbar-item>
            </template>
            <template #right>
                <custom-switch
                    v-if="$ability.hasPermission('customer:Update')"
                    v-model="enabledUpdate"
                >
                    <span class="text-theme-inverse">🖊</span>
                </custom-switch>
                <dropdown-popover
                    v-if="
                        $ability.hasPermission('customer:Delete') ||
                        $ability.hasPermission('customer:DeleteDefinitive')
                    "
                    class="mr-2"
                    shadow
                    border-thick
                >
                    <custom-avatar
                        pointer
                        size="large"
                        circle
                        class="bg-theme-primary text-theme-inverse"
                    >
                        <span> ⚙ </span>
                    </custom-avatar>
                    <template #tooltip>
                        <div class="flex flex-col items-start py-3">
                            <div
                                v-if="$ability.hasPermission('customer:Delete')"
                                class="md:m-2 m-1"
                            >
                                🔴
                                <button
                                    class="font-bold btn-a md:ml-2 ml-1"
                                    aria-label="Eliminar cliente"
                                    @click="deleteItemComponent"
                                >
                                    Eliminar cliente
                                </button>
                            </div>
                            <div
                                v-if="
                                    $ability.hasPermission(
                                        'customer:DeleteDefinitive'
                                    )
                                "
                                class="md:m-2 m-1"
                            >
                                🛑
                                <button
                                    class="font-bold btn-a md:ml-2 ml-1"
                                    aria-label="Eliminar cliente definitivamente"
                                    @click="deleteItemDefinitelyComponent"
                                >
                                    Eliminación definitiva
                                </button>
                            </div>
                        </div>
                    </template>
                </dropdown-popover>
            </template>
        </custom-navbar>

        <div v-show="['general', 'home'].includes(activeTab)">
            <h3 class="text-theme-primary my-3 text-lg">Datos básicos 👩‍🦱</h3>
            <div class="flex flex-wrap mt-3">
                <div class="w-full md:w-1/2 mt-4 px-2 flex flex-col my-2">
                    <custom-input
                        v-model="itemComponent.firstName"
                        label="Nombres"
                        class="w-full"
                        :disabled="!enabledUpdate"
                    />
                </div>
                <div class="w-full md:w-1/2 mt-4 px-2 flex flex-col my-2">
                    <custom-input
                        v-model="itemComponent.lastName"
                        label="Apellidos"
                        class="w-full"
                        :disabled="!enabledUpdate"
                    />
                </div>

                <div class="w-full md:w-1/2 mt-4 px-2 flex flex-col my-2">
                    <custom-input
                        v-model="itemComponent.phone"
                        class="w-full"
                        label="Celular de contacto"
                        type="number"
                        :disabled="!enabledUpdate"
                    />
                </div>

                <div class="w-full md:w-1/2 mt-4 px-2 flex flex-col my-2">
                    <custom-input
                        v-model="itemComponent.email"
                        class="w-full"
                        label="Email"
                        :disabled="!enabledUpdate"
                    />
                </div>
                <div class="w-full md:w-1/2 mt-4 px-2 flex flex-col my-2">
                    <identification-type-selector
                        v-model="itemComponent.identificationType"
                        :enabled="enabledUpdate"
                    />
                </div>

                <div class="w-full md:w-1/2 mt-4 px-2 flex flex-col my-2">
                    <custom-input
                        v-model="itemComponent.identificationNumber"
                        class="w-full"
                        label="Número de Identificación"
                        :disabled="!enabledUpdate"
                    />
                </div>
            </div>
            <p class="text-xs text-theme-muted text-center mt-4">
                Antes de registrar a un cliente, asegúrate de tener su
                autorización para tratar sus datos.
            </p>
            <div class="flex justify-center my-4">
                <app-button
                    :disabled="!enabledUpdate"
                    @click="updateBasicDataCustomer"
                >
                    Actualizar información básica
                </app-button>
            </div>
        </div>

        <div v-if="activeTab === 'locations'">
            <h3 class="text-theme-primary my-3 text-lg">
                Direcciones registradas 🌎
            </h3>

            <div
                v-if="locationsUser.length"
                class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
            >
                <div
                    v-for="(item, index) in locationsUser"
                    :key="index"
                    class="bg-theme-card border border-theme-border rounded-lg shadow-sm p-4"
                >
                    <div class="flex justify-between items-center mb-2">
                        <span class="text-lg font-semibold">
                            {{ item.address }}
                        </span>
                    </div>
                    <div class="w-full flex flex-col">
                        <div class="flex flex-col my-2">
                            <strong> Nombre de la persona que recibe </strong>
                            <span class="text-uppercase mt-2 ml-2">
                                {{ item.receiverName }}
                            </span>
                        </div>
                        <div class="flex flex-col my-2">
                            <strong>Celular persona que recibe </strong>
                            <span class="mt-2 ml-2">
                                {{ item.receiverPhone }}
                            </span>
                        </div>
                        <div class="flex flex-col my-2">
                            <strong>Ciudad </strong>
                            <span class="mt-2 ml-2">
                                {{ item.city }} ({{ item.cityCode }})
                            </span>
                        </div>
                        <div class="flex flex-col my-2">
                            <strong>Dirección </strong>
                            <span class="mt-2 ml-2">
                                {{ item.address }} / Barrio
                                {{ item.neighborhood }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else>
                <strong>
                    No se han registrado direcciones para el cliente.
                </strong>
            </div>
        </div>

        <div v-if="activeTab === 'orders'">
            <h3 class="text-theme-primary my-3 text-lg">Ordenes 💳</h3>
            <div class="my-4">
                <custom-table
                    v-show="loadedOrder"
                    ref="selectableTable"
                    :fields="$data.$fields"
                    :items="ordersUser"
                    :per-page="ordersUser.length"
                    responsive
                    outlined
                    no-border-collapse
                    hover
                    class="w-full"
                    items-empty-message="Nuestro sistema NO encuentra resultados. Intenta modificar los filtros de búsqueda."
                >
                    <template #cell(id)="data">
                        <div class="flex items-center">
                            <a
                                :href="`/app/orders/detail/${data.item.id}`"
                                target="_blank"
                                rel="noopener"
                                class="text-center btn btn-stack-primary p-1"
                            >
                                <span> {{ data.item.id }}</span>
                            </a>
                        </div>
                    </template>
                    <template #cell(deliveryStatusMessage)="data">
                        <span
                            class="text-center"
                            :class="
                                $data.$ordersStatusesMessageClass[
                                    data.item.deliveryStatus
                                ]
                            "
                        >
                            {{ data.item.deliveryStatusMessage }}
                        </span>
                    </template>
                    <template #cell(deliveryOptionMessage)="data">
                        <span
                            class="text-center"
                            :class="
                                data.item.deliveryOption === 'home'
                                    ? 'text-info'
                                    : ''
                            "
                        >
                            {{ data.item.deliveryOptionMessage }}
                        </span>
                    </template>
                    <template #cell(paymentStatusMessage)="data">
                        <span
                            class="text-center"
                            :class="
                                $data.$paymentStatusesMessageClass[
                                    data.item.paymentStatus
                                ]
                            "
                        >
                            {{ data.item.paymentStatusMessage }}
                        </span>
                    </template>
                    <template #cell(paymentMethodMessage)="data">
                        <span class="text-center">
                            {{ data.item.paymentMethodMessage }}
                        </span>
                    </template>
                    <template #cell(total)="data">
                        <span class="text-center">
                            {{ data.item.total | money }}
                        </span>
                    </template>
                    <template #cell(createdAt)="data">
                        <span class="text-center">
                            {{ data.item.createdAt | date }}
                        </span>
                    </template>
                    <template #cell(updatedAt)="data">
                        <span class="text-center">
                            {{ data.item.updatedAt | date }}
                        </span>
                    </template>

                    <template #cell(deliveryAt)="data">
                        <span class="text-center">
                            {{ data.item.deliveryAt | dateSimple }}
                        </span>
                    </template>
                </custom-table>
            </div>
        </div>
    </main>
</template>

<script>
import tabQuerySyncMixin from "@/mixins/tabQuerySyncMixin";
import { mapActions, mapGetters } from "vuex";

import { paymentStatusesMessageClass } from "@/common/fields/orders-payment-status-codes";
import { ordersStatusesMessageClass } from "@/common/fields/orders-status-codes";

import IdentificationTypeSelector from "@/components/users/IdentificationTypeSelector.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import CustomTable from "@/components/utils/CustomTable.vue";
import UuidDisplay from "@/components/utils/UuidDisplay.vue";

export default {
    name: "DetailCustomers",
    components: {
        IdentificationTypeSelector,
        CustomTable,
        CustomInput,
        UuidDisplay
    },
    mixins: [tabQuerySyncMixin],
    data: () => ({
        $ordersStatusesMessageClass: ordersStatusesMessageClass,
        $paymentStatusesMessageClass: paymentStatusesMessageClass,
        $fields: [
            { key: "id", label: "ID", sortable: false },
            {
                key: "deliveryOptionMessage",
                label: "Opción de entrega",
                sortable: false
            },
            {
                key: "deliveryStatusMessage",
                label: "Estado",
                sortable: true
            },
            {
                key: "paymentStatusMessage",
                label: "Estado de pago",
                sortable: false
            },
            {
                key: "paymentMethodMessage",
                label: "Método de pago",
                sortable: false
            },
            { key: "total", label: "Total", sortable: true },
            { key: "deliveryAt", label: "📆 Entrega", sortable: true },
            { key: "createdAt", label: "📆 Creación", sortable: true },
            { key: "updatedAt", label: "📆 Actualización", sortable: true }
        ],
        itemComponent: {
            id: "",
            identificationType: "cc",
            identificationNumber: "",
            firstName: "Cargando...",
            lastName: "Cargando...",
            email: "name@doomain.com",
            phone: "..."
        },
        loadedOrder: false,
        activeTab: "general",
        $tabsWhitelist: ["general", "orders", "locations"],
        enabledUpdate: false,
        isLoading: false,
        ordersUser: [],
        locationsUser: [],
        permissions: [],
        id: ""
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"])
    },
    watch: {
        activeTab(value) {
            if (value === "orders") {
                this.listOrders();
            }
            if (value === "locations") {
                this.listLocations();
            }
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if ("id" in this.$route.params) {
            if (!(await verifyPagePermission("customer:Get", "/app/customers")))
                return;
            this.id = this.$route.params.id;
            await this.getBaseCustomer();
        }
    },
    methods: {
        ...mapActions("customers", [
            "updateCustomer",
            "getCustomerById",
            "listCustomerLocations",
            "listCustomerOrdersPaginated",
            "deleteCustomer",
            "deleteCustomerDefinitively"
        ]),
        async getBaseCustomer() {
            this.isLoading = true;
            this.$loading.show();
            try {
                this.itemComponent = await this.getCustomerById({
                    id: this.id
                });
                this.isLoading = false;
                this.$loading.hide();
            } catch (error) {
                this.isLoading = false;
                this.$loading.hide();
                this.isLoading = false;
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
                if (error.statusCode === 404) {
                    this.$router.push("/app");
                }
            }
        },
        async updateBasicDataCustomer() {
            this.isLoading = true;
            this.$loading.show();
            try {
                const {
                    firstName,
                    id,
                    identificationNumber,
                    identificationType,
                    lastName,
                    phone
                } = this.itemComponent;
                await this.updateCustomer({
                    id,
                    firstName,
                    lastName,
                    phone,
                    ...(identificationNumber ? { identificationNumber } : null),
                    ...(identificationType ? { identificationType } : null)
                });
                this.$sound.play();
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: "Actualización exitosa",
                    icon: "success",
                    showCloseButton: true,
                    timer: 3000
                });
                this.$loading.hide();
                this.isLoading = false;
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$loading.hide();
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async listOrders() {
            this.$loading.show();
            try {
                const { items } = await this.listCustomerOrdersPaginated({
                    page: 1,
                    customerId: this.id,
                    complete: false
                });
                this.ordersUser = items;
                this.loadedOrder = true;
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async listLocations() {
            this.$loading.show();
            try {
                this.locationsUser = await this.listCustomerLocations({
                    page: 1,
                    customerId: this.id
                });
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async deleteItemComponent() {
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: "Seguro deseas eliminar el cliente ?",
                text: "Recuerda se va a eliminar tu cliente y puedes perder tiempo 🗑️",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#02913f",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Si",
                cancelButtonText: "No"
            });
            if (dialogResult.isConfirmed) {
                this.isLoading = true;
                this.$loading.show();
                try {
                    await this.deleteCustomer({ id: this.itemComponent.id });
                    this.$toast.success({
                        title: `Cliente # ${this.itemComponent.id} eliminado`
                    });
                    this.$router.push("/app/customers");
                    this.$loading.hide();
                    this.isLoading = false;
                } catch (error) {
                    this.$loading.hide();
                    this.isLoading = false;
                    this.$loading.hide();
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: error.title,
                        text: error.message,
                        icon: error.icon
                    });
                }
            }
        },
        async deleteItemDefinitelyComponent() {
            const dialogResult = await this.$swal.fire({
                background: this.backgroundColor,
                title: "Seguro ?",
                text: "Recuerda se va a eliminar tu cliente definitivamente y puedes perder tiempo 🗑️",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#02913f",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Si",
                cancelButtonText: "No"
            });
            if (dialogResult.isConfirmed) {
                this.isLoading = true;
                this.$loading.show();
                try {
                    await this.deleteCustomerDefinitively({
                        id: this.itemComponent.id
                    });
                    this.$toast.success({
                        title: `Cliente # ${this.itemComponent.id} eliminado`
                    });
                    this.$router.push("/app/customers");
                    this.$loading.hide();
                    this.isLoading = false;
                } catch (error) {
                    this.$loading.hide();
                    this.isLoading = false;
                    this.$loading.hide();
                    this.$swal.fire({
                        background: this.backgroundColor,
                        title: error.title,
                        text: error.message,
                        icon: error.icon
                    });
                }
            }
        }
    }
};
</script>
